import React from 'react'

import {
  getBreakdownId,
  isRatingBreakdown,
  isSentimentBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'
import { StackedBarChart } from 'features/charts-v2/components/StackedBarChart'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { quantizeBarValuesToDisplayPrecision } from 'features/charts-v2/utils/axis-calculations'

export class StackedBarChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.BAR_HORIZONTAL_STACKED
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = [
    METRICS.RECORD_COUNT.id,
    METRICS.SENTENCE_COUNT.id,
  ]

  public transform(props: ChartProps): TransformedChartData {
    return quantizeBarValuesToDisplayPrecision(
      this.transformRaw(props),
      props.selectedMetrics,
    )
  }

  private transformRaw(props: ChartProps): TransformedChartData {
    const breakdowns = (props.selectedBreakdowns || []).filter(
      (b): b is Exclude<typeof b, null> => b !== null,
    )

    // Handle 0 breakdowns: show single bar with total
    if (breakdowns.length === 0) {
      return dataTransformationService.transformForTotalOnly(
        props.data!,
        props.selectedMetrics,
      )
    }

    // Check if this is sentiment stacking data (single domain, multiple values)
    // This is a special case where the API aggregates sentiment data into values array
    // Only treat as sentiment data if one of the breakdowns is actually "Sentiment"
    const hasSentimentBreakdown = breakdowns.some(isSentimentBreakdown)

    if (
      hasSentimentBreakdown &&
      props.data &&
      dataTransformationService.isSentimentStackingData(props.data)
    ) {
      return dataTransformationService.transformForSentimentStacking(
        props.data,
        props.selectedMetrics,
        breakdowns,
        props.timeBucket,
        props.sortDirection,
      )
    }

    // For single breakdown, use basic chart transformation
    if (breakdowns.length === 1) {
      return dataTransformationService.transformForBasicChart(
        props.data!,
        props.selectedMetrics,
        props.timeBucket,
        props.sortDirection,
      )
    }

    // For multiple breakdowns, use the breakdown order to determine y-axis vs stacking
    // First breakdown = Y-axis (categories), Second breakdown = Stacking
    // This respects user's choice of breakdown order
    // Convert breakdowns to string names for the transformation
    const breakdownNames = breakdowns.map(getBreakdownId)

    const result = dataTransformationService.transformForOrderedStackedBar(
      props.data!,
      breakdownNames,
      props.selectedMetrics,
      props.timeBucket,
      props.sortDirection,
    )

    return result
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData } & {
      onSegmentClick?: (
        segmentData: {
          primaryBreakdown: string
          stackingBreakdown?: string
          value: number
          payload: Record<string, unknown>
        },
        event: React.MouseEvent,
      ) => void
    },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      onDataPointHover,
      onSegmentHover,
      selectedMetrics,
      selectedBreakdowns,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      showDatapointLabels = true,
      xAxisOverflow = 'expand',
      yAxisOverflow = 'expand',
      yAxisDomain,
      xAxisDomain,
    } = props

    const breakdowns = selectedBreakdowns || []
    const isRatingStacking = isRatingBreakdown(
      (breakdowns[1] as Parameters<typeof isRatingBreakdown>[0]) ?? null,
    )
    // Mirrors isRatingStacking — when the segment breakdown is sentiment,
    // apply the team's sentiment palette regardless of which data-encoding
    // path the chart's data came in on (FILTER aggregations vs GROUP BY).
    const isSentimentStacking = isSentimentBreakdown(
      (breakdowns[1] as Parameters<typeof isSentimentBreakdown>[0]) ?? null,
    )

    // For 0 breakdowns, render as a simple bar chart showing total
    if (breakdowns.length === 0) {
      return (
        <StackedBarChart
          data={transformedData.chartData as never[]}
          selectedMetrics={selectedMetrics}
          onDataPointHover={onDataPointHover}
          onSegmentHover={onSegmentHover}
          breakdownCategories={['count']}
          onSegmentClick={onSegmentClick}
          fontSizePx={fontSizePx}
          showXAxisLabel={showXAxisLabel}
          showYAxisLabel={showYAxisLabel}
          showDatapointLabels={showDatapointLabels}
          xAxisOverflow={xAxisOverflow}
          yAxisOverflow={yAxisOverflow}
          yAxisDomain={yAxisDomain}
          xAxisDomain={xAxisDomain}
        />
      )
    }

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    // Check if this is sentiment stacking data
    // Only treat as sentiment data if one of the breakdowns is actually "Sentiment"
    const hasSentimentBreakdown = breakdowns.some(isSentimentBreakdown)
    const isSentimentData =
      hasSentimentBreakdown &&
      props.data &&
      dataTransformationService.isSentimentStackingData(props.data)

    if (isSentimentData) {
      // For sentiment stacking, use the sentiment categories from metadata
      return (
        <StackedBarChart
          data={transformedData.chartData as never[]}
          selectedMetrics={selectedMetrics}
          onDataPointHover={onDataPointHover}
          onSegmentHover={onSegmentHover}
          breakdownCategories={transformedData.metadata?.breakdownCategories} // ['positive', 'neutral', 'negative']
          breakdownCategoryLabels={
            transformedData.metadata?.breakdownCategoryLabels
          }
          isSentimentStacking={isSentimentStacking}
          onSegmentClick={onSegmentClick}
          fontSizePx={fontSizePx}
          showXAxisLabel={showXAxisLabel}
          showYAxisLabel={showYAxisLabel}
          showDatapointLabels={showDatapointLabels}
          xAxisOverflow={xAxisOverflow}
          yAxisOverflow={yAxisOverflow}
          yAxisDomain={yAxisDomain}
          xAxisDomain={xAxisDomain}
        />
      )
    }

    // For single breakdown, show as regular bar chart (no stacking)
    if (breakdowns.length === 1) {
      return (
        <StackedBarChart
          data={transformedData.chartData as never[]}
          selectedMetrics={selectedMetrics}
          onDataPointHover={onDataPointHover}
          onSegmentHover={onSegmentHover}
          breakdownCategories={['count']} // Single category for basic bar chart
          onSegmentClick={onSegmentClick}
          fontSizePx={fontSizePx}
          showXAxisLabel={showXAxisLabel}
          showYAxisLabel={showYAxisLabel}
          showDatapointLabels={showDatapointLabels}
          xAxisOverflow={xAxisOverflow}
          yAxisOverflow={yAxisOverflow}
          yAxisDomain={yAxisDomain}
          xAxisDomain={xAxisDomain}
        />
      )
    }

    return (
      <StackedBarChart
        data={transformedData.chartData as never[]}
        selectedMetrics={selectedMetrics}
        onDataPointHover={onDataPointHover}
        onSegmentHover={onSegmentHover}
        breakdownCategories={transformedData.metadata?.breakdownCategories}
        breakdownCategoryLabels={
          transformedData.metadata?.breakdownCategoryLabels
        }
        isRatingStacking={isRatingStacking}
        isSentimentStacking={isSentimentStacking}
        onSegmentClick={onSegmentClick}
        fontSizePx={fontSizePx}
        showXAxisLabel={showXAxisLabel}
        showYAxisLabel={showYAxisLabel}
        showDatapointLabels={showDatapointLabels}
        xAxisOverflow={xAxisOverflow}
        yAxisOverflow={yAxisOverflow}
        yAxisDomain={yAxisDomain}
        xAxisDomain={xAxisDomain}
      />
    )
  }

  public getTitle(): string {
    return 'Stacked Breakdown'
  }
}
